import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  attachCatalogVideo,
  detachCatalogVideo,
  getCatalogPlayback,
  getWorkoutCatalog,
} from "../services/workout-catalog.service";
import { getPlaybackUrl, listWorkoutAssets } from "../services/upload.service";
import { CatalogExercise } from "../types/workout-catalog.type";
import { WorkoutVideoAsset } from "../types/upload.type";

type LinkFilter = "all" | "linked" | "unlinked";

const WorkoutCatalog: React.FC = () => {
  const [exercises, setExercises] = useState<CatalogExercise[]>([]);
  const [assets, setAssets] = useState<WorkoutVideoAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [linkFilter, setLinkFilter] = useState<LinkFilter>("all");

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingExercise, setLinkingExercise] = useState<CatalogExercise | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [linking, setLinking] = useState(false);

  const [playerOpen, setPlayerOpen] = useState(false);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [playbackLoading, setPlaybackLoading] = useState(false);
  const [playbackTitle, setPlaybackTitle] = useState("");

  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [catalogRes, assetsRes] = await Promise.all([
        getWorkoutCatalog(),
        listWorkoutAssets(),
      ]);
      setExercises(catalogRes.exercises ?? []);
      setAssets(assetsRes.assets ?? []);
    } catch {
      setError("Failed to load catalog or uploaded videos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredExercises = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return exercises.filter((ex) => {
      const matchesSearch =
        !q ||
        ex.id.toLowerCase().includes(q) ||
        ex.name.toLowerCase().includes(q) ||
        ex.muscle_group.toLowerCase().includes(q);
      const isLinked = Boolean(ex.video_asset_id);
      const matchesFilter =
        linkFilter === "all" ||
        (linkFilter === "linked" && isLinked) ||
        (linkFilter === "unlinked" && !isLinked);
      return matchesSearch && matchesFilter;
    });
  }, [exercises, searchQuery, linkFilter]);

  const openLinkDialog = (exercise: CatalogExercise) => {
    setLinkingExercise(exercise);
    setSelectedAssetId(exercise.video_asset_id ?? assets[0]?.id ?? "");
    setLinkDialogOpen(true);
  };

  const handleLink = async () => {
    if (!linkingExercise || !selectedAssetId) return;
    setLinking(true);
    setError(null);
    try {
      await attachCatalogVideo(linkingExercise.id, selectedAssetId);
      setSuccess(`Linked video to ${linkingExercise.name}.`);
      setLinkDialogOpen(false);
      await loadData();
    } catch {
      setError("Failed to link video. Ensure you have admin or manager access.");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (exercise: CatalogExercise) => {
    if (!window.confirm(`Unlink video from "${exercise.name}"?`)) return;
    setUnlinkingId(exercise.id);
    setError(null);
    try {
      await detachCatalogVideo(exercise.id);
      setSuccess(`Unlinked video from ${exercise.name}.`);
      await loadData();
    } catch {
      setError("Failed to unlink video.");
    } finally {
      setUnlinkingId(null);
    }
  };

  const handlePlayCatalog = async (exercise: CatalogExercise) => {
    setPlaybackTitle(exercise.name);
    setPlayerOpen(true);
    setPlaybackUrl(null);
    setPlaybackLoading(true);
    setError(null);
    try {
      const playback = await getCatalogPlayback(exercise.id);
      setPlaybackUrl(playback.playback_url);
    } catch {
      setError("Failed to load playback URL.");
      setPlayerOpen(false);
    } finally {
      setPlaybackLoading(false);
    }
  };

  const handlePlayAsset = async (asset: WorkoutVideoAsset) => {
    setPlaybackTitle(asset.original_name);
    setPlayerOpen(true);
    setPlaybackUrl(null);
    setPlaybackLoading(true);
    setError(null);
    try {
      const playback = await getPlaybackUrl(asset.file_path);
      setPlaybackUrl(playback.playback_url);
    } catch {
      setError("Failed to load playback URL.");
      setPlayerOpen(false);
    } finally {
      setPlaybackLoading(false);
    }
  };

  const handleClosePlayer = () => {
    setPlayerOpen(false);
    setPlaybackUrl(null);
    setPlaybackTitle("");
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Catalog Videos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Link coach-uploaded workout videos to exercises from the catalog. Links are
        stored in the database and available to mobile users anytime.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Uploaded videos
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Filename</TableCell>
              <TableCell>Uploaded by</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Linked exercises</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : assets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No workout videos uploaded yet. Coaches upload from Upload Videos.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              assets.map((asset) => (
                <TableRow key={asset.id}>
                  <TableCell>{asset.original_name}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                    {asset.created_by}
                  </TableCell>
                  <TableCell>
                    {new Date(asset.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {asset.linked_catalog_ids.length > 0
                      ? asset.linked_catalog_ids.join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handlePlayAsset(asset)}
                      title="Preview"
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Exercise catalog
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search exercises..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={linkFilter}
            onChange={(e) => setLinkFilter(e.target.value as LinkFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="linked">Linked</MenuItem>
            <MenuItem value="unlinked">Unlinked</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Muscle group</TableCell>
              <TableCell>Equipment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredExercises.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No exercises match your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredExercises.map((exercise) => (
                <TableRow key={exercise.id}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {exercise.id}
                  </TableCell>
                  <TableCell>{exercise.name}</TableCell>
                  <TableCell>{exercise.muscle_group}</TableCell>
                  <TableCell>{exercise.equipment}</TableCell>
                  <TableCell>
                    {exercise.video_asset_id ? (
                      <Chip label="Linked" color="success" size="small" />
                    ) : (
                      <Chip label="No video" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {exercise.video_asset_id && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handlePlayCatalog(exercise)}
                        title="Preview linked video"
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => openLinkDialog(exercise)}
                      title="Link video"
                      disabled={assets.length === 0}
                    >
                      <LinkIcon />
                    </IconButton>
                    {exercise.video_asset_id && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleUnlink(exercise)}
                        disabled={unlinkingId === exercise.id}
                        title="Unlink video"
                      >
                        {unlinkingId === exercise.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <LinkOffIcon />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={linkDialogOpen}
        onClose={() => !linking && setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Link video to {linkingExercise?.name ?? "exercise"}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Uploaded video</InputLabel>
            <Select
              label="Uploaded video"
              value={selectedAssetId}
              onChange={(e) => setSelectedAssetId(e.target.value)}
            >
              {assets.map((asset) => (
                <MenuItem key={asset.id} value={asset.id}>
                  {asset.original_name} ({asset.id.slice(0, 8)}…)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)} disabled={linking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLink}
            disabled={linking || !selectedAssetId}
          >
            {linking ? "Linking…" : "Link"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={playerOpen} onClose={handleClosePlayer} maxWidth="md" fullWidth>
        <DialogTitle>{playbackTitle || "Video preview"}</DialogTitle>
        <DialogContent>
          {playbackLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : playbackUrl ? (
            <Box
              component="video"
              src={playbackUrl}
              controls
              autoPlay
              sx={{ width: "100%", borderRadius: 1, bgcolor: "black" }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePlayer}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkoutCatalog;
